/**
 * Utilidades para cálculos de préstamos y bonos
 * Incluye: amortización francesa, capital fijo, bonos bullet
 */

/* ═══════════════════════════════════════════
   BONOS
   ═══════════════════════════════════════════ */

export const FRECUENCIAS_BONO = {
  mensual:    { label: 'Mensual',    periodosPorAno: 12, mesesPorPeriodo: 1  },
  trimestral: { label: 'Trimestral', periodosPorAno: 4,  mesesPorPeriodo: 3  },
  semestral:  { label: 'Semestral',  periodosPorAno: 2,  mesesPorPeriodo: 6  },
  anual:      { label: 'Anual',      periodosPorAno: 1,  mesesPorPeriodo: 12 },
};

/**
 * Genera tabla de un bono bullet con tasa anual porcentual.
 * El inversionista recibe cupones periódicos (solo interés) y al vencimiento
 * recupera el capital completo + último cupón.
 */
export function generarTablaBono(principal, tasaAnual, plazoMeses, fechaInicio, frecuencia = 'mensual', diaPago = null) {
  const freq         = FRECUENCIAS_BONO[frecuencia];
  const numPeriodos  = Math.round(plazoMeses / freq.mesesPorPeriodo);
  const tasaPer      = tasaAnual / 100 / freq.periodosPorAno;
  const cupon        = Math.round(principal * tasaPer * 100) / 100;
  const base         = new Date(fechaInicio);
  const cuotas       = [];

  for (let i = 1; i <= numPeriodos; i++) {
    const venc = new Date(base.getFullYear(), base.getMonth() + i * freq.mesesPorPeriodo, 1);
    const dia  = diaPago
      ? Math.min(diaPago, new Date(venc.getFullYear(), venc.getMonth() + 1, 0).getDate())
      : base.getDate();
    venc.setDate(dia);

    const esFinal          = i === numPeriodos;
    const capitalDevuelto  = esFinal ? principal : 0;

    cuotas.push({
      numero:           i,
      fechaVencimiento: new Date(venc),
      monto:            Math.round((cupon + capitalDevuelto) * 100) / 100,
      capital:          capitalDevuelto,
      intereses:        cupon,
      saldo:            esFinal ? 0 : principal,
      esFinal,
      estado:           'pendiente',
      fechaPago:        null,
      montoPagado:      0,
    });
  }
  return cuotas;
}

/**
 * Genera tabla de un bono bullet con monto de interés total fijo acordado.
 */
export function generarTablaBonoCapital(principal, interesTotal, plazoMeses, fechaInicio, frecuencia = 'mensual', diaPago = null) {
  const freq        = FRECUENCIAS_BONO[frecuencia];
  const numPeriodos = Math.round(plazoMeses / freq.mesesPorPeriodo);
  const cupon       = Math.round(interesTotal / numPeriodos);
  const base        = new Date(fechaInicio);
  const cuotas      = [];

  for (let i = 1; i <= numPeriodos; i++) {
    const venc = new Date(base.getFullYear(), base.getMonth() + i * freq.mesesPorPeriodo, 1);
    const dia  = diaPago
      ? Math.min(diaPago, new Date(venc.getFullYear(), venc.getMonth() + 1, 0).getDate())
      : base.getDate();
    venc.setDate(dia);

    const esFinal         = i === numPeriodos;
    const capitalDevuelto = esFinal ? principal : 0;

    cuotas.push({
      numero:           i,
      fechaVencimiento: new Date(venc),
      monto:            cupon + capitalDevuelto,
      capital:          capitalDevuelto,
      intereses:        cupon,
      saldo:            esFinal ? 0 : principal,
      esFinal,
      estado:           'pendiente',
      fechaPago:        null,
      montoPagado:      0,
    });
  }
  return cuotas;
}

/**
 * Calcula la cuota mensual usando la fórmula de amortización francesa
 * @param {number} principal - Monto del préstamo
 * @param {number} tasaAnual - Tasa de interés anual (ej: 5.5 para 5.5%)
 * @param {number} plazoMeses - Duración del préstamo en meses
 * @returns {number} - Cuota mensual
 */
export function calcularCuotaMensual(principal, tasaAnual, plazoMeses) {
  if (principal <= 0 || tasaAnual < 0 || plazoMeses <= 0) return 0;

  const tasaMensual = tasaAnual / 100 / 12;

  // Si tasa es 0%, dividir uniformemente
  if (tasaMensual === 0) {
    return principal / plazoMeses;
  }

  const numerador = principal * tasaMensual * Math.pow(1 + tasaMensual, plazoMeses);
  const denominador = Math.pow(1 + tasaMensual, plazoMeses) - 1;

  return numerador / denominador;
}

/**
 * Genera tabla de amortización completa
 * @param {number} principal - Monto del préstamo
 * @param {number} tasaAnual - Tasa anual
 * @param {number} plazoMeses - Plazo en meses
 * @param {Date} fechaInicio - Fecha de inicio del préstamo
 * @param {number|null} diaPagoCuota - Día del mes en que vencen las cuotas (1-31). Si es null, usa el día de fechaInicio.
 * @returns {Array} - Array de cuotas con detalles
 */
export function generarTablaAmortizacion(principal, tasaAnual, plazoMeses, fechaInicio, diaPagoCuota = null) {
  const tasaMensual = tasaAnual / 100 / 12;
  const cuotaMensual = calcularCuotaMensual(principal, tasaAnual, plazoMeses);

  let saldoPendiente = principal;
  const cuotas = [];
  const base = new Date(fechaInicio);

  for (let i = 1; i <= plazoMeses; i++) {
    // Calcular interés del período
    const intereses = saldoPendiente * tasaMensual;

    // Capital = cuota - interés
    const capital = Math.round((cuotaMensual - intereses) * 100) / 100;

    // Actualizar saldo
    saldoPendiente = Math.max(0, saldoPendiente - capital);

    // Calcular fecha de vencimiento: mes base + i meses, con el día configurado
    const venc = new Date(base.getFullYear(), base.getMonth() + i, 1);
    const dia  = diaPagoCuota
      ? Math.min(diaPagoCuota, new Date(venc.getFullYear(), venc.getMonth() + 1, 0).getDate())
      : base.getDate();
    venc.setDate(dia);

    cuotas.push({
      numero: i,
      fechaVencimiento: new Date(venc),
      monto: Math.round(cuotaMensual * 100) / 100,
      capital: Math.round(capital * 100) / 100,
      intereses: Math.round(intereses * 100) / 100,
      estado: 'pendiente',
      fechaPago: null,
      montoPagado: 0,
    });
  }

  return cuotas;
}

/**
 * Calcula intereses acumulados hasta una fecha
 * @param {Array} cuotas - Tabla de amortización
 * @returns {number} - Total de intereses
 */
export function calcularInteresesAcumulados(cuotas) {
  return cuotas
    .filter(c => c.estado === 'pagada')
    .reduce((sum, c) => sum + (c.intereses || 0), 0);
}

/**
 * Obtiene la próxima cuota pendiente
 * @param {Array} cuotas - Tabla de amortización
 * @returns {Object|null} - Próxima cuota o null
 */
export function obtenerProximaCuota(cuotas) {
  return cuotas.find(c => c.estado !== 'pagada') || null;
}

/**
 * Calcula días de mora
 * @param {Date} fechaVencimiento - Fecha de vencimiento de cuota
 * @returns {number} - Días de atraso (0 si no está vencido)
 */
export function calcularDiasMora(fechaVencimiento) {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  // Soporta Firestore Timestamp, Date, string ISO
  const vencimiento = fechaVencimiento?.toDate
    ? fechaVencimiento.toDate()
    : new Date(fechaVencimiento);
  vencimiento.setHours(0, 0, 0, 0);

  const diferencia = hoy.getTime() - vencimiento.getTime();
  const dias = Math.floor(diferencia / (1000 * 60 * 60 * 24));

  return Math.max(0, dias);
}

/**
 * Calcula cuota con interés en capital fijo (personas naturales)
 * El interés total es un monto fijo acordado, no una tasa porcentual
 * @param {number} principal - Monto del préstamo
 * @param {number} interesTotal - Monto total de interés acordado
 * @param {number} plazoMeses - Plazo en meses
 * @returns {number} - Cuota mensual
 */
export function calcularCuotaConInteresCapital(principal, interesTotal, plazoMeses) {
  if (principal <= 0 || plazoMeses <= 0) return 0;
  return Math.round((principal + (interesTotal || 0)) / plazoMeses);
}

/**
 * Genera tabla de amortización con interés en capital fijo
 * Usado exclusivamente para préstamos de persona natural con monto de interés acordado
 * @param {number} principal - Monto del préstamo
 * @param {number} interesTotal - Monto total de interés fijo acordado
 * @param {number} plazoMeses - Plazo en meses
 * @param {Date} fechaInicio - Fecha de inicio
 * @param {number|null} diaPagoCuota - Día del mes en que vencen las cuotas (1-31)
 * @returns {Array} - Tabla de cuotas
 */
export function generarTablaConInteresCapital(principal, interesTotal, plazoMeses, fechaInicio, diaPagoCuota = null) {
  if (principal <= 0 || plazoMeses <= 0) return [];

  const cuotaMensual = calcularCuotaConInteresCapital(principal, interesTotal, plazoMeses);
  const interesPorCuota = Math.round((interesTotal || 0) / plazoMeses);
  const capitalPorCuota = Math.round(principal / plazoMeses);

  const cuotas = [];
  const base = new Date(fechaInicio);
  let saldoPendiente = principal;

  for (let i = 1; i <= plazoMeses; i++) {
    saldoPendiente = Math.max(0, saldoPendiente - capitalPorCuota);

    // Calcular fecha de vencimiento con el día configurado
    const venc = new Date(base.getFullYear(), base.getMonth() + i, 1);
    const dia  = diaPagoCuota
      ? Math.min(diaPagoCuota, new Date(venc.getFullYear(), venc.getMonth() + 1, 0).getDate())
      : base.getDate();
    venc.setDate(dia);

    cuotas.push({
      numero: i,
      fechaVencimiento: new Date(venc),
      monto: cuotaMensual,
      capital: capitalPorCuota,
      intereses: interesPorCuota,
      saldo: saldoPendiente,
      estado: 'pendiente',
      fechaPago: null,
      montoPagado: 0,
    });
  }

  return cuotas;
}

/**
 * Valida datos de préstamo
 * @param {Object} datos - Objeto con datos del préstamo
 * @returns {Object} - {valido: boolean, errores: Array}
 */
export function validarPrestamo(datos) {
  const errores = [];

  if (!datos.acreedor?.nombre?.trim()) {
    errores.push('Nombre del acreedor es requerido');
  }

  if (!datos.montoOriginal || datos.montoOriginal <= 0) {
    errores.push('Monto debe ser mayor a 0');
  }

  // Validación de interés según tipo
  const esPersonaNatural = datos.tipo === 'persona_natural';
  const usaInteresCapital = esPersonaNatural && datos.tipoInteresPersonal === 'monto_capital';

  if (usaInteresCapital) {
    // Interés en capital fijo: puede ser 0 (sin interés)
    if (datos.interesCapital !== undefined && datos.interesCapital < 0) {
      errores.push('El monto de interés no puede ser negativo');
    }
  } else {
    if (datos.tasaInteres === undefined || datos.tasaInteres < 0 || datos.tasaInteres > 100) {
      errores.push('Tasa de interés debe estar entre 0% y 100%');
    }
  }

  if (!datos.plazo || datos.plazo <= 0 || datos.plazo > 600) {
    errores.push('Plazo debe estar entre 1 y 600 meses');
  }

  if (!datos.fechaInicio) {
    errores.push('Fecha de inicio es requerida');
  }

  return {
    valido: errores.length === 0,
    errores,
  };
}

/**
 * Obtiene resumen de préstamo actual
 * @param {Object} prestamo - Documento del préstamo
 * @returns {Object} - Resumen con datos clave
 */
export function obtenerResumenPrestamo(prestamo) {
  const proximaCuota = obtenerProximaCuota(prestamo.cuotas || []);
  const diasMora = proximaCuota ? calcularDiasMora(proximaCuota.fechaVencimiento) : 0;

  const cuotasPagadas = (prestamo.cuotas || []).filter(c => c.estado === 'pagada').length;
  const cuotasVencidas = (prestamo.cuotas || []).filter(c => c.estado === 'vencida').length;
  const cuotasPendientes = (prestamo.cuotas || []).filter(c => c.estado === 'pendiente').length;

  return {
    montoOriginal: prestamo.montoOriginal,
    montoActual: prestamo.montoActual,
    tasaInteres: prestamo.tasaInteres,
    proximaCuota: proximaCuota,
    diasMora: diasMora,
    estado: prestamo.estado,
    cuotasPagadas,
    cuotasVencidas,
    cuotasPendientes,
    totalCuotas: prestamo.cuotas?.length || 0,
    interesesAcumulados: prestamo.interesesAcumulados || 0,
  };
}

/**
 * Calcula saldo de préstamo después de pago
 * @param {number} saldoActual - Saldo actual
 * @param {number} montoPagado - Monto pagado
 * @returns {number} - Nuevo saldo
 */
export function calcularNuevoSaldo(saldoActual, montoPagado) {
  return Math.max(0, saldoActual - montoPagado);
}

/**
 * Obtiene alertas del préstamo
 * @param {Object} prestamo - Documento del préstamo
 * @returns {Array} - Array de alertas
 */
export function obtenerAlertasPrestamo(prestamo) {
  const alertas = [];

  if (prestamo.estado === 'pagado') {
    alertas.push({
      tipo: 'success',
      mensaje: 'Préstamo pagado completamente',
    });
    return alertas;
  }

  if (prestamo.estado === 'incobrable') {
    alertas.push({
      tipo: 'error',
      mensaje: 'Préstamo marcado como incobrable',
    });
    return alertas;
  }

  const proximaCuota = obtenerProximaCuota(prestamo.cuotas || []);
  if (proximaCuota) {
    const diasMora = calcularDiasMora(proximaCuota.fechaVencimiento);

    if (diasMora > 0) {
      alertas.push({
        tipo: 'error',
        mensaje: `Cuota ${proximaCuota.numero} vencida hace ${diasMora} días`,
      });
    } else {
      const diasFaltantes = Math.ceil(
        (new Date(proximaCuota.fechaVencimiento).getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24)
      );

      if (diasFaltantes <= 7 && diasFaltantes > 0) {
        alertas.push({
          tipo: 'warning',
          mensaje: `Cuota ${proximaCuota.numero} vence en ${diasFaltantes} días`,
        });
      }
    }
  }

  return alertas;
}
